import React, { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, RotateCcw } from "lucide-react";

// Tap-to-count number line for K-2 add/subtract. Tap a number to land on it,
// then hop right (+1) to add or left (-1) to subtract. Not graded — a thinking aid.
export default function NumberLine({ max = 20 }) {
  const [pos, setPos] = useState(null);
  const [hops, setHops] = useState(0);
  const nums = Array.from({ length: max + 1 }, (_, i) => i);

  const land = (n) => { setPos(n); setHops(0); };
  const hop = (d) => {
    setPos((p) => {
      const start = p === null ? 0 : p;
      const next = Math.max(0, Math.min(max, start + d));
      if (next !== start) setHops((h) => h + 1);
      return next;
    });
  };
  const reset = () => { setPos(null); setHops(0); };

  return (
    <div className="rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] p-4" data-testid="number-line">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-[var(--ns-text)]">
          {pos === null ? "Tap a number to start" : <span data-testid="number-line-readout">You're on <span className="text-[var(--ns-primary)] font-bold text-lg">{pos}</span>{hops > 0 && ` · ${hops} hop${hops === 1 ? "" : "s"}`}</span>}
        </div>
        <button onClick={reset} data-testid="number-line-reset" className="flex items-center gap-1 text-xs font-semibold text-[var(--ns-muted)]"><RotateCcw size={13} /> Reset</button>
      </div>

      <div className="relative overflow-x-auto ns-scroll pb-2">
        <div className="flex items-center gap-1 min-w-max px-1">
          {nums.map((n) => {
            const active = pos === n;
            return (
              <button key={n} data-testid={`nl-${n}`} onClick={() => land(n)}
                className="relative flex flex-col items-center shrink-0" style={{ width: 34 }}>
                {active && (
                  <motion.div layoutId="nl-marker" className="absolute -top-6 text-lg" transition={{ type: "spring", stiffness: 400, damping: 26 }}>🐸</motion.div>
                )}
                <span className="grid place-items-center rounded-full font-bold transition-colors"
                  style={{
                    width: 30, height: 30,
                    background: active ? "var(--ns-primary)" : "var(--ns-surface-subtle)",
                    color: active ? "#fff" : "var(--ns-text)",
                    outline: active ? "3px solid var(--ns-primary-light)" : "none",
                  }}>{n}</span>
                <span className="mt-1 h-2 w-0.5" style={{ background: "var(--ns-border)" }} />
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <button onClick={() => hop(-1)} data-testid="number-line-minus"
          className="flex items-center justify-center gap-2 rounded-[var(--ns-radius)] font-semibold text-white"
          style={{ minHeight: "var(--ns-touch)", background: "#D96B43" }}><Minus size={18} /> Hop back</button>
        <button onClick={() => hop(1)} data-testid="number-line-plus"
          className="flex items-center justify-center gap-2 rounded-[var(--ns-radius)] font-semibold text-white"
          style={{ minHeight: "var(--ns-touch)", background: "var(--ns-primary)" }}><Plus size={18} /> Hop forward</button>
      </div>
      <p className="mt-2 text-xs text-[var(--ns-subtle)] text-center">Tip: land on the first number, then hop forward to add or back to subtract.</p>
    </div>
  );
}

export const COUNTING_SKILLS = ["k.count_20", "k.compare", "g1.add_20", "g1.sub_20"];
export const isCountingSkill = (id) => COUNTING_SKILLS.includes(id);
